// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { KeyboardEventHandler, useCallback, useEffect, useRef, useState } from 'react';

import { Portal, useReducedMotion } from '@amzn/awsui-component-toolkit/internal';

import { usePortalModeClasses } from '../internal/hooks/use-portal-mode-classes';
import Arrow from '../popover/arrow';
import PopoverBody from '../popover/body';
import PopoverContainer from '../popover/container';

interface TooltipProps {
  children?: React.ReactNode;
  content?: React.ReactNode;
  position?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
  controlledOpen?: boolean;
}

const DEFAULT_OPEN_TIMEOUT_IN_MS = 120;

export default function Tooltip({ children, content, position = 'right', className, controlledOpen }: TooltipProps) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const isReducedMotion = useReducedMotion(ref);
  const { open, triggerProps } = useTooltipOpen(controlledOpen, isReducedMotion ? 0 : DEFAULT_OPEN_TIMEOUT_IN_MS);
  const portalClasses = usePortalModeClasses(ref);

  return (
    <span ref={ref} {...triggerProps} className={className}>
      {children}
      {open && (
        <Portal>
          <span className={portalClasses}>
            <PopoverContainer
              size="small"
              fixedWidth={false}
              position={position}
              trackRef={ref}
              arrow={position => <Arrow position={position} />}
              renderWithPortal={true}
              zIndex={7000}
            >
              <PopoverBody
                dismissButton={false}
                dismissAriaLabel={undefined}
                header={null}
                onDismiss={() => {}}
                overflowVisible="both"
              >
                <span data-testid="button-dropdown-disabled-reason" role="tooltip">
                  {content}
                </span>
              </PopoverBody>
            </PopoverContainer>
          </span>
        </Portal>
      )}
    </span>
  );
}

function useTooltipOpen(controlledOpen: boolean | undefined, timeout: number) {
  const [isOpen, setIsOpen] = useState(false);

  // The delayed effect is aborted in case the component unmounts. We cannot use the conventional clearTimeout()
  // as it causes cleanup of a legitimate state update when used with React 18+ strict mode.
  const timeoutRef = useRef<number>();
  const timeoutAbortRef = useRef(false);
  useEffect(() => {
    timeoutAbortRef.current = false;
    return () => {
      timeoutAbortRef.current = true;
    };
  }, []);

  const close = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setIsOpen(false);
  }, []);

  const open = useCallback(() => {
    if (!timeoutAbortRef.current) {
      setIsOpen(true);
    }
  }, []);

  const openDelayed = useCallback(() => {
    timeoutRef.current = setTimeout(open, timeout);
  }, [open, timeout]);

  useEffect(() => {
    if (controlledOpen === true) {
      openDelayed();
    }
    if (controlledOpen === false) {
      close();
    }
  }, [controlledOpen, openDelayed, close]);

  const onKeyDown: KeyboardEventHandler = event => {
    if (isOpen && isEscape(event.key)) {
      event.preventDefault();
      event.stopPropagation();
      close();
    }
  };

  const onFocus = openDelayed;
  const onBlur = close;

  return {
    open: isOpen,
    triggerProps: {
      onBlur,
      onFocus,
      onKeyDown,
    },
  };
}

const isEscape = (key: string) => key === 'Escape' || key === 'Esc';
